import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "../../context/Context";
import { fetchStoresAPI } from "../../utils/api/stores";
import { notifyError } from "../../utils/notify";
import { CEO_LEVEL } from "../core/roles";
import { normalizeId } from "../core/scope";
import { useAccess } from "./useAccess";

// Reports, tickets and other records point at a restaurant by its Mongo `_id`,
// while `user.between` (what scope checks compare) holds the public `id`.
// This hook resolves either to the public id using the stores list the app
// already loads (`fetchStoresAPI`, filtered by the backend to the user's
// assignments). A reference that is not in that list resolves to `null`, and a
// scoped check without an id is denied: identity is never guessed.
const directories = new Map(); // key -> Promise<Map<ref, publicId>> (successes and in-flight only)
const failures = new Map(); // key -> { at, error } (NA-033)

// After a failed load the same request is not repeated by every component that
// mounts: the failure is replayed for this long unless the user retries (the
// Retry action, or the browser coming back online).
export const DIRECTORY_FAILURE_COOLDOWN_MS = 30000;

const UNAVAILABLE_NOTICE = {
  en: "Could not verify your restaurant access. Actions on restaurants stay unavailable until it loads.",
  ar: "تعذّر التحقق من صلاحياتك على المطاعم. تبقى الإجراءات غير متاحة حتى يتم التحميل.",
};

const loadDirectory = (token, ids, { force = false } = {}) => {
  const key = `${token}|${JSON.stringify(ids)}`;
  if (force) failures.delete(key);
  const failed = failures.get(key);
  if (failed && Date.now() - failed.at < DIRECTORY_FAILURE_COOLDOWN_MS) {
    return Promise.reject(failed.error);
  }
  if (!directories.has(key)) {
    const promise = (async () => {
      // `strict`: failures throw (no browser alert); this hook reports them.
      const stores = await fetchStoresAPI(token, ids, { strict: true });
      if (!Array.isArray(stores)) {
        const error = new Error("stores unavailable");
        error.kind = "invalid";
        throw error;
      }
      const map = new Map();
      stores.forEach((store) => {
        const publicId = normalizeId(store?.id);
        if (!publicId) return;
        map.set(publicId, publicId);
        const mongoId = normalizeId(store?._id);
        if (mongoId) map.set(mongoId, publicId);
      });
      failures.delete(key);
      return map;
    })();
    promise.catch((error) => {
      directories.delete(key);
      failures.set(key, { at: Date.now(), error });
      notifyError(UNAVAILABLE_NOTICE, { key: "restaurant-directory-unavailable" });
    });
    directories.set(key, promise);
  }
  return directories.get(key);
};

export const clearRestaurantDirectory = () => {
  directories.clear();
  failures.clear();
};

const mapFromStores = (stores) => {
  const map = new Map();
  stores.forEach((store) => {
    const publicId = normalizeId(store?.id);
    if (!publicId) return;
    map.set(publicId, publicId);
    const mongoId = normalizeId(store?._id);
    if (mongoId) map.set(mongoId, publicId);
  });
  return map;
};

/**
 * Access checks for records that belong to a restaurant.
 *
 *   const { canFor, ready } = useRestaurantAccess();
 *   if (!canFor("report.entry.delete", row.storeId)) return;   // handler guard
 *
 * `canFor(permission, ref)` = `can(permission, { restaurantId: publicId(ref) })`.
 * Pass `stores` (a stores list the component already loaded, e.g. a `stores` prop)
 * to resolve against it instead of fetching; an empty list means "not loaded yet".
 * Scoped permissions are denied until the directory has loaded and whenever the
 * reference cannot be resolved, so nothing scoped runs on a stale or unverified
 * identity. ceo-level roles are never restricted by scope (they pass without an id).
 * `canForAll(permission, refs)` requires every reference to pass (a list that is
 * empty or has an unresolvable entry only passes for global roles).
 */
export function useRestaurantAccess(stores) {
  const access = useAccess();
  const { storeAccess, isAuthenticated } = useContext(Context) || {};
  const idsKey = JSON.stringify(storeAccess || []);
  // { key, map, error }: `map` once loaded, `error` after a failed load.
  const [directory, setDirectory] = useState({ key: null, map: null, error: null });
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("auth-token") : null;
  const key = `${token}|${idsKey}`;
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const needsDirectory =
    !Array.isArray(stores) && isAuthenticated && Boolean(token) && !CEO_LEVEL.includes(access.role);

  const load = useCallback(
    (force) =>
      loadDirectory(token, JSON.parse(idsKey), { force })
        .then((map) => aliveRef.current && setDirectory({ key, map, error: null }))
        .catch((error) => aliveRef.current && setDirectory({ key, map: null, error })),
    [token, idsKey, key],
  );

  useEffect(() => {
    // ceo-level roles are never restricted by scope: no directory needed.
    // A given stores list is resolved locally: no request.
    if (!needsDirectory) return undefined;
    load(false);
    return undefined;
  }, [needsDirectory, load]);

  const givenMap = useMemo(
    () => (Array.isArray(stores) && stores.length ? mapFromStores(stores) : null),
    [stores],
  );
  const current = directory.key === key ? directory : null;
  const map = Array.isArray(stores) ? givenMap : current?.map || null;
  const ready = Boolean(map);
  // loading | ready | error | idle (nothing to load for this identity)
  const directoryStatus = ready
    ? "ready"
    : !needsDirectory
      ? "idle"
      : current?.error
        ? "error"
        : "loading";

  // Retry now (ignores the failure cooldown); also when the browser reconnects.
  const retry = useCallback(() => {
    if (needsDirectory) load(true);
  }, [needsDirectory, load]);
  useEffect(() => {
    if (directoryStatus !== "error") return undefined;
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [directoryStatus, retry]);

  const publicIdOf = useCallback(
    (ref) => {
      const normalized = normalizeId(ref);
      return (normalized && map?.get(normalized)) || null;
    },
    [map],
  );

  const canFor = useCallback(
    (permission, ref) => {
      if (!access.isAccessReady) return false;
      const restaurantId = publicIdOf(ref);
      // Global roles pass without an id; everyone else needs a verified one.
      return access.can(permission, restaurantId ? { restaurantId } : {});
    },
    [access, publicIdOf],
  );

  const canForAll = useCallback(
    (permission, refs) => {
      const list = Array.isArray(refs) ? refs : [];
      if (list.length === 0 || list.some((ref) => !normalizeId(ref))) return canFor(permission, undefined);
      return list.every((ref) => canFor(permission, ref));
    },
    [canFor],
  );

  return useMemo(
    () => ({ ...access, ready, directoryStatus, retry, publicIdOf, canFor, canForAll }),
    [access, ready, directoryStatus, retry, publicIdOf, canFor, canForAll],
  );
}

export default useRestaurantAccess;
